export type ChatStreamErrorPayload = {
  type?: string
  error?: string | { message?: string; type?: string }
  message?: string
  title?: string
  provider?: string
  keySource?: 'workspace' | 'platform' | 'missing' | 'unknown'
  guidance?: string | null
  providerMessage?: string
}

/** Chrome mid-stream body reset often surfaces as TypeError: "network error". */
export function isChatStreamTransportError(err: unknown): boolean {
  if (!(err instanceof Error)) return false
  const m = (err.message || '').trim()
  if (/^network error$/i.test(m)) return true
  if (/Failed to fetch|Load failed|NetworkError|ERR_HTTP2|HTTP2_PROTOCOL|stream.*reset/i.test(m)) {
    return true
  }
  if (err.name === 'TypeError' && /network|fetch|load failed/i.test(m)) return true
  return false
}

export function formatChatStreamTransportError(): {
  title: string
  description: string
  inlineMarkdown: string
} {
  const title = 'Connection dropped'
  const description =
    'The chat stream was reset mid-response (often during long tool turns). This is usually a proxy/stream timeout, not a missing Supabase session. Try sending again - shorter asks (one task per message) are more reliable.'
  return {
    title,
    description,
    inlineMarkdown: `**${title}**\n\n${description}`,
  }
}

/**
 * Turn SSE `error` events (and legacy raw provider JSON strings) into readable UI copy.
 */
export function formatChatStreamError(parsed: ChatStreamErrorPayload): {
  title: string
  description: string
  inlineMarkdown: string
} {
  if (typeof parsed.message === 'string' && parsed.message.trim()) {
    const title =
      typeof parsed.title === 'string' && parsed.title.trim()
        ? parsed.title.trim()
        : 'Chat error'
    const description = parsed.message.trim()
    // Mid-stream drops sometimes arrive as SSE error payloads with bare "network error"
    if (isChatStreamTransportError(new Error(description))) {
      return formatChatStreamTransportError()
    }
    return {
      title,
      description,
      inlineMarkdown: `**${title}**\n\n${description}`,
    }
  }

  const code =
    typeof parsed.error === 'string'
      ? parsed.error
      : typeof parsed.error === 'object' && parsed.error?.message
        ? parsed.error.message
        : null

  const raw = code || JSON.stringify(parsed.error ?? 'Unknown error')

  // Legacy: full JSON blob in error string
  if (raw.includes('credit balance') || raw.includes('purchase credits')) {
    return {
      title: 'Anthropic credits exhausted',
      description:
        'The Claude API key in use has insufficient credits. If this workspace uses BYOK, top up that key or ask your workspace admin. Otherwise try another model.',
      inlineMarkdown:
        '**Anthropic credits exhausted**\n\nThe Claude API key in use has insufficient credits. If this workspace uses BYOK, top up that key or ask your workspace admin. Otherwise try another model.',
    }
  }

  // Try embedded JSON
  const jsonMatch = raw.match(/\{[\s\S]+\}/)
  if (jsonMatch) {
    try {
      const body = JSON.parse(jsonMatch[0]) as {
        error?: { message?: string }
        message?: string
      }
      const msg = body.error?.message || body.message
      if (msg) {
        return {
          title: 'Chat error',
          description: msg,
          inlineMarkdown: `**Chat error**\n\n${msg}`,
        }
      }
    } catch {
      // ignore
    }
  }

  return {
    title: 'Chat error',
    description: raw,
    inlineMarkdown: `**Chat error**\n\n${raw}`,
  }
}
